/**
 * Demo Setup Page
 *
 * Platform-admin only screen for managing demo environments without git access.
 *
 * Sections:
 *  1. Pre-flight checklist — links to required setup steps
 *  2. Seed data loader     — tab-per-vertical preview then single Run button
 *  3. Sample file downloads — per-file and full-ZIP downloads
 *  4. Interactive runbook  — step-by-step guide for each vertical
 *  5. Teardown             — wipe all demo data
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FlaskConical,
  Download,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronDown,
  ChevronRight,
  Leaf,
  Flame,
  Database,
  FileArchive,
  FileText,
  File,
  AlertTriangle,
  RefreshCw,
  ExternalLink,
  Package,
  Info,
  Building2,
} from 'lucide-react';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import { demoApi, DemoSeedFile, DemoSampleFile, DemoOrg } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type Vertical = 'oil-gas' | 'agrotech';
type SeedGroup = 'base' | 'agrotech' | 'oil_gas' | 'platform';
type SeedTabKey = 'agrotech' | 'oil_gas' | 'platform' | 'all';

interface RunbookStep {
  num: number;
  title: string;
  url?: string;
  description: string;
  tip?: string;
}

interface SeedRunResult {
  filename: string;
  status: 'ok' | 'error' | 'running';
  error?: string;
}

// ─── Runbook data ─────────────────────────────────────────────────────────────

const RUNBOOK_OIL_GAS: RunbookStep[] = [
  { num: 1,  title: 'Prerequisites', description: 'Confirm production URL loads, platform-admin credentials are known, and sample files are downloaded (see Section 3 of this page).' },
  { num: 2,  title: 'Load Sample Files', description: 'Download all Oil & Gas files below. Keep the folder accessible — you will pick files from it during Step 6.' },
  { num: 3,  title: 'Provision Demo Organisation', url: '/platform-admin/organizations', description: 'Create org: TransNiger Energy Ltd · admin@transniger-demo.local · UTC · USD · metric · WGS84.' },
  { num: 4,  title: 'Enable Oil & Gas Vertical', url: '/platform-admin/verticals', description: 'Click Oil & Gas Intelligence card → find TransNiger Energy Ltd → toggle ON.', tip: 'Adds mvp3_oil_gas to enabled_modules. The Oil & Gas sidebar section becomes visible on next login.' },
  { num: 5,  title: 'Configure Roles & Permissions', url: '/platform-admin/roles', description: 'Verify org_admin, safety_officer, pilot, and analyst roles exist. Optionally create an OGI Operations Manager custom role.' },
  { num: 6,  title: 'Register Demo Users', url: '/register', description: 'In a new incognito window, register: Alex Okonkwo (pilot@transniger-demo.local), Priya Nair (safety@transniger-demo.local), Marcus Adeyemi (response@transniger-demo.local). Password: Demo@1234.' },
  { num: 7,  title: 'Approve Users & Assign Roles', url: '/org-admin/users', description: 'Switch to org admin session → Settings → User Management → Join Requests. Approve each user and assign: Alex → pilot, Priya → safety_officer, Marcus → analyst.' },
  { num: 8,  title: 'Register Approved Assets', url: '/settings/approved-assets', description: 'Add DJI Matrice 300 RTK (serial: OGI-DRONE-M300-001) and FLIR Vue Pro R 640 (serial: OGI-SENSOR-FLIR-001).', tip: 'The backend validates serial numbers during mission ingestion. Assets must be registered first.' },
  { num: 9,  title: 'Define Org Hierarchy', url: '/enterprise/hierarchy', description: 'Create: TransNiger Operations (division) → Niger Delta Region (region) → Trans-Niger Corridor A (facility) → OGI Patrol Team (team).' },
  { num: 10, title: 'Step 1 — Register a Pipeline Corridor Boundary', url: '/oil-gas/boundaries', description: 'Add Corridor: Trans-Niger Corridor A · TNL-A-001 · Crude Oil · 100 m buffer · monitoring weekly. Draw polygon over Niger Delta (~4.81°N, 7.05°E to 4.85°N, 7.09°E).' },
  { num: 11, title: 'Step 2 — Satellite Temporal Analysis', url: '/oil-gas/temporal', description: 'Select corridor → 6 months ago to today → Fetch Imagery. Look for LST spikes or NDVI die-off.', tip: 'For demo without live GEE: results may be pre-populated from seed data. Proceed regardless.' },
  { num: 12, title: 'Steps 3 & 4 — Anomaly Alerts & Verification', url: '/oil-gas/anomalies', description: 'Filter by Corridor A, Severity: Critical. Click Run Monitoring. Open an anomaly → verify as Confirmed (Drone Survey Required).' },
  { num: 13, title: 'Step 5 — OGI Survey Mission Planner', url: '/oil-gas/survey-planner', description: 'Anomaly is now "investigating". Click Launch Mission Planner. Select assets → Generate waypoints → Create Mission.' },
  { num: 14, title: 'Step 6 — Upload OGI Flight Data', url: '/oil-gas/flight-analysis', description: 'Select the survey mission → Upload files: ogi_pipeline_thermal_orthomosaic.tif (image_thermal), ogi_pipeline_rgb_orthomosaic.tif (image_rgb), ogi_pipeline_flight_log.csv (flight_log), ogi_pipeline_flight_path.kml (flight_log), ogi_pipeline_lidar_stub.las (lidar).', tip: 'Upload ogi_pipeline_thermal.jpg separately as a supplementary thermal still.' },
  { num: 15, title: 'Step 6b — Plume & Leak Analyzer', url: '/oil-gas/plume-leak', description: 'Run Plume Analysis on the uploaded thermal data. Review leak rate estimate (kg/hr) and confidence score.' },
  { num: 16, title: 'Facility Safety & Flare Monitoring (Supplemental)', url: '/oil-gas/facility-safety', description: 'Browse the facility safety page to show flare monitoring and compressor station thermal readings from seed data.' },
  { num: 17, title: 'Step 7 — Analysis Review (Safety Officer Gate)', url: '/oil-gas/analysis-review', description: 'Log in as Priya Nair (safety officer). Review AI findings. Click Confirm — Regulation Breach or Override. This gates Step 8.' },
  { num: 18, title: 'Step 8a — Response Planning & ERP Dispatch', url: '/oil-gas/response-planning', description: 'Mission is now "confirmed_critical". Create a response plan, assign Marcus, generate an ERP work order (SAP/Maximo stub).' },
  { num: 19, title: 'Step 8b — Incident Closure & SHA-256 Seal', url: '/oil-gas/incident-closure', description: 'After ERP dispatched: mark incident as Closed. The platform generates a SHA-256 immutable seal on all records.' },
  { num: 20, title: 'Regulatory Reporting & Export', url: '/oil-gas/regulatory-reporting', description: 'Generate a certified PDF report. Show the SHA-256 seal, chain-of-custody, and regulatory submission status.' },
  { num: 21, title: 'Value Realisation & ROI Dashboard', url: '/oil-gas/value-realization', description: 'Show loss volume avoided, detection time vs manual baseline, carbon avoided metric.' },
  { num: 22, title: 'ERP Integration Audit Trail', url: '/oil-gas/erp-integration', description: 'Show the webhook log for SAP S/4HANA work-order dispatch and confirmation receipt.' },
];

const RUNBOOK_AGROTECH: RunbookStep[] = [
  { num: 1,  title: 'Prerequisites', description: 'Confirm production URL loads, platform-admin credentials are known, and sample files are downloaded (see Section 3 of this page).' },
  { num: 2,  title: 'Load Sample Files', description: 'Download all AgroTech files below. Keep the folder accessible — you will pick files from it during Step 6.' },
  { num: 3,  title: 'Provision Demo Organisation', url: '/platform-admin/organizations', description: 'Create org: GreenCrest Agro Pvt Ltd · admin@greencrest-demo.local · Asia/Kolkata · INR · metric · WGS84.' },
  { num: 4,  title: 'Enable AgroTech Vertical', url: '/platform-admin/verticals', description: 'Click AgroTech Estate Intelligence → find GreenCrest Agro Pvt Ltd → toggle ON.', tip: 'Adds mvp1_agrotech to enabled_modules. AgroTech sidebar section becomes visible on next login.' },
  { num: 5,  title: 'Configure Roles & Permissions', url: '/platform-admin/roles', description: 'Verify org_admin, agronomist, pilot, and analyst roles exist. Optionally create an Estate Manager custom role.' },
  { num: 6,  title: 'Register Demo Users', url: '/register', description: 'In a new incognito window, register: Ravi Menon (pilot@greencrest-demo.local), Dr Ananya Krishnan (agronomist@greencrest-demo.local), Suresh Pillai (operations@greencrest-demo.local). Password: Demo@1234.' },
  { num: 7,  title: 'Approve Users & Assign Roles', url: '/org-admin/users', description: 'Switch to org admin session → User Management → Join Requests. Approve: Ravi → pilot, Dr Ananya → agronomist, Suresh → analyst.' },
  { num: 8,  title: 'Register Approved Assets', url: '/settings/approved-assets', description: 'Add DJI Agras T40 (serial: AGRO-DRONE-T40-001), MicaSense RedEdge-P (serial: AGRO-SENSOR-MS-001), FLIR Vue Pro R 320 (serial: AGRO-CAMERA-FLIR-001).' },
  { num: 9,  title: 'Define Org Hierarchy', url: '/enterprise/hierarchy', description: 'Create: GreenCrest Estate (division) → South Karnataka Region (region) → Sugarcane Block S7 (plot) → Drainage Team (team).' },
  { num: 10, title: 'Step 1 — Register a Field Boundary', url: '/agrotech/fields', description: 'Add Field: Sugarcane Block S7 · Sugarcane Co 86032 · Canal irrigation · Monitoring weekly. Draw polygon ~12.30°N, 76.65°E to 12.34°N, 76.70°E.' },
  { num: 11, title: 'Step 2 — Satellite Temporal Analysis', url: '/agrotech/temporal', description: 'Select Sugarcane Block S7 → 90 days ago → 20% max cloud → Fetch Imagery. Look for NDVI < 0.35 or elevated NDWI.', tip: 'For demo without live GEE: results may be pre-populated from seed data. Proceed regardless.' },
  { num: 12, title: 'Steps 3 & 4 — Anomaly Alerts & Verification', url: '/agrotech/anomalies', description: 'Run Monitoring. Open the waterlogging/vegetation-decline anomaly → Verify → Confirmed (Drone Survey Required).' },
  { num: 13, title: 'Step 5 — Survey Mission Planner', url: '/agrotech/survey-planner', description: 'Anomaly is "investigating". Click Launch Mission Planner → select assets → Generate waypoints → Create Mission.' },
  { num: 14, title: 'Step 6 — Upload Drone Flight Data & Trigger AI', url: '/agrotech/flight-analysis', description: 'Select mission → Upload: sugarcane_s7_rgb_orthomosaic.tif (image_rgb), sugarcane_s7_multispectral_ndvi.tif (image_multispectral), sugarcane_s7_thermal.jpg (image_thermal), sugarcane_s7_flight_log.csv (flight_log), sugarcane_s7_flight_path.kml (flight_log), sugarcane_s7_lidar_stub.las (lidar). Run AI Analysis.' },
  { num: 15, title: 'Step 7 — Agronomist Analysis Review Gate', url: '/agrotech/analysis-review', description: 'Log in as Dr Ananya Krishnan. Review AI diagnosis (waterlogging / phytophthora). Click Confirm or Override. This gates Step 8.' },
  { num: 16, title: 'Step 8 — Remediation Mission Planning & Dispatch', url: '/agrotech/remediation', description: 'Create a remediation mission. Select DJI Agras T40 sprayer. Set treatment (Fosetyl-aluminium + Metalaxyl-M). Dispatch.' },
  { num: 17, title: 'Step 8.6 — Treatment Efficacy Verification', url: '/agrotech/efficacy', description: 'After treatment: re-fetch satellite imagery. Compare before/after NDVI. Record efficacy score.' },
  { num: 18, title: 'Step 9 — Ask Nabhos (Conversational Intelligence)', url: '/agrotech/ask', description: 'Ask: "Which fields had NDVI below 0.4 in the last 30 days?" or "What is the current waterlogging risk for Block S7?"', tip: 'Nabhos synthesises satellite time-series, anomaly history, and mission outcomes to answer in natural language.' },
  { num: 19, title: 'Crop Health Reports', url: '/agrotech/reports', description: 'Generate and download a certified PDF Crop Health Report with satellite analytics, AI findings, and agronomist sign-off.' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

const VERTICAL_SEED_GROUPS: Record<SeedTabKey, SeedGroup[]> = {
  agrotech: ['base', 'agrotech'],
  oil_gas:  ['base', 'oil_gas'],
  platform: ['base', 'platform'],
  all:      ['base', 'agrotech', 'oil_gas', 'platform'],
};

const GROUP_BADGE: Record<SeedGroup, { label: string; color: string }> = {
  base:     { label: 'Base',      color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
  agrotech: { label: 'AgroTech',  color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  oil_gas:  { label: 'Oil & Gas', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  platform: { label: 'Platform',  color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
};

// What each seed tab is for — shown in the preview panel before the user commits to running.
interface SeedTabMeta {
  key: SeedTabKey;
  label: string;
  icon: React.ReactNode;
  purpose: string;        // one sentence: why does a salesperson need this?
  screens: string[];      // UI screens this data populates
  tabColor: string;       // active tab highlight classes
  btnColor: string;       // run-button classes
}

function fileIcon(filename: string) {
  if (filename.endsWith('.csv'))  return <FileText size={15} className="text-green-600" />;
  if (filename.endsWith('.kml'))  return <FileText size={15} className="text-blue-600" />;
  if (filename.endsWith('.las'))  return <File size={15} className="text-purple-600" />;
  if (filename.endsWith('.tif'))  return <File size={15} className="text-orange-500" />;
  if (filename.endsWith('.jpg'))  return <File size={15} className="text-pink-500" />;
  return <File size={15} className="text-gray-400" />;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface RunbookViewerProps {
  steps: RunbookStep[];
  vertical: Vertical;
}

function RunbookViewer({ steps }: RunbookViewerProps) {
  const [completed, setCompleted] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set([1, 2, 3]));

  const toggle = (num: number) =>
    setExpanded((prev) => { const s = new Set(prev); s.has(num) ? s.delete(num) : s.add(num); return s; });

  const toggleComplete = (num: number) =>
    setCompleted((prev) => { const s = new Set(prev); s.has(num) ? s.delete(num) : s.add(num); return s; });

  return (
    <div className="space-y-1.5">
      {steps.map((step) => {
        const done = completed.has(step.num);
        const open = expanded.has(step.num);
        return (
          <div
            key={step.num}
            className={`border rounded-lg transition-colors ${
              done
                ? 'border-green-200 bg-green-50 dark:border-green-800/40 dark:bg-green-950/20'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900'
            }`}
          >
            {/* Header row */}
            <div className="flex items-center gap-2 px-3 py-2.5 cursor-pointer select-none" onClick={() => toggle(step.num)}>
              {/* Checkbox */}
              <button
                onClick={(e) => { e.stopPropagation(); toggleComplete(step.num); }}
                className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                  done ? 'bg-green-500 border-green-500' : 'border-gray-300 dark:border-gray-600 hover:border-green-400'
                }`}
              >
                {done && <CheckCircle2 size={13} className="text-white" />}
              </button>

              {/* Step number */}
              <span className="flex-shrink-0 w-6 text-center text-xs font-bold text-gray-400 dark:text-gray-500">
                {step.num}
              </span>

              {/* Title */}
              <span className={`flex-1 text-sm font-medium ${done ? 'line-through text-gray-400' : 'text-gray-800 dark:text-gray-200'}`}>
                {step.title}
              </span>

              {/* Link */}
              {step.url && (
                <Link
                  to={step.url}
                  onClick={(e) => e.stopPropagation()}
                  className="flex-shrink-0 flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline mr-1"
                >
                  Open <ExternalLink size={11} />
                </Link>
              )}

              {/* Expand toggle */}
              {open ? <ChevronDown size={15} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={15} className="text-gray-400 flex-shrink-0" />}
            </div>

            {/* Body */}
            {open && (
              <div className="px-10 pb-3 text-sm text-gray-600 dark:text-gray-400 space-y-2">
                <p>{step.description}</p>
                {step.tip && (
                  <div className="flex gap-2 p-2 rounded-md bg-blue-50 dark:bg-blue-950/20 border border-blue-100 dark:border-blue-800/30">
                    <Info size={14} className="text-blue-500 mt-0.5 flex-shrink-0" />
                    <span className="text-xs text-blue-700 dark:text-blue-300">{step.tip}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Main page component ──────────────────────────────────────────────────────

// ─── Seed tab metadata ────────────────────────────────────────────────────────
// Defined here (not at module level) so JSX is valid — but hoisted outside the
// component so it is stable across renders.

const SEED_TABS: SeedTabMeta[] = [
  {
    key: 'agrotech',
    label: 'AgroTech Demo',
    icon: <Leaf size={14} />,
    purpose:
      'Populates every AgroTech screen with realistic multi-crop field data — ' +
      'eight field boundaries across India, satellite time-series imagery, anomaly alerts, ' +
      'survey missions, AI analysis results, and a completed remediation cycle. ' +
      'Without this data the AgroTech screens show empty states.',
    screens: [
      'Fields map', 'Temporal Analysis', 'Anomaly Alerts',
      'Survey Mission Planner', 'Flight Analysis', 'Analysis Review',
      'Remediation Missions', 'Treatment Efficacy', 'Crop Health Reports',
    ],
    tabColor: 'border-green-500 text-green-700 dark:text-green-400',
    btnColor: 'bg-green-600 hover:bg-green-700 text-white',
  },
  {
    key: 'oil_gas',
    label: 'Oil & Gas Demo',
    icon: <Flame size={14} />,
    purpose:
      'Populates every Oil & Gas screen with realistic pipeline corridor data — ' +
      'four corridor boundaries, five anomaly alerts across severity levels, ' +
      'seven OGI survey missions progressing through every pipeline stage, ' +
      'SHA-256 sealed incident closures, ERP work orders, and regulatory reports. ' +
      'Without this data the Oil & Gas screens show empty states.',
    screens: [
      'Dashboard', 'Corridor Boundaries', 'Temporal Analysis', 'Anomaly Alerts',
      'Survey Mission Planner', 'Flight Analysis', 'Plume & Leak Analyzer',
      'Analysis Review', 'Response Planning', 'Incident Closure',
      'Facility Safety', 'Regulatory Reporting', 'Value Realisation', 'ERP Integration',
      'Upstream Exploration',
    ],
    tabColor: 'border-blue-500 text-blue-700 dark:text-blue-400',
    btnColor: 'bg-blue-600 hover:bg-blue-700 text-white',
  },
  {
    key: 'platform',
    label: 'Platform Admin',
    icon: <Package size={14} />,
    purpose:
      'Populates the Platform Admin screens with three additional demo organisations ' +
      '(AgriMax, PetroNova, TerraScan), ten users, eight registered assets, ' +
      'membership requests, webhooks, integrity seals, and AI audit decisions. ' +
      'Use this to demo the multi-tenant administration story.',
    screens: [
      'Platform Dashboard', 'Organisation Directory', 'Membership Requests',
      'Platform Analytics', 'User Management', 'User Management Audit',
      'Notifications Center', 'Webhooks Configuration',
      'Integrity Dashboard', 'Audit Trail Viewer',
    ],
    tabColor: 'border-amber-500 text-amber-700 dark:text-amber-400',
    btnColor: 'bg-amber-600 hover:bg-amber-700 text-white',
  },
  {
    key: 'all',
    label: 'Everything',
    icon: <Database size={14} />,
    purpose:
      'Runs all seed files in order — base hardware registry, all AgroTech data, ' +
      'all Oil & Gas data, and all Platform Admin data. ' +
      'Use for a comprehensive full-platform demo or when setting up a fresh environment.',
    screens: ['All screens across every vertical'],
    tabColor: 'border-gray-700 text-gray-800 dark:text-gray-200',
    btnColor: 'bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 text-white',
  },
];

// ─── Main page component ──────────────────────────────────────────────────────

export default function DemoSetupPage() {
  const [seeds, setSeeds] = useState<DemoSeedFile[]>([]);
  const [sampleFiles, setSampleFiles] = useState<DemoSampleFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Org picker state
  const [orgs, setOrgs] = useState<DemoOrg[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');

  // Seed section state
  const [seedTab, setSeedTab] = useState<SeedTabKey>('agrotech');
  const [seedResults, setSeedResults] = useState<SeedRunResult[]>([]);
  const [seeding, setSeeding] = useState(false);

  // Teardown state
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);

  // Runbook tab
  const [runbookTab, setRunbookTab] = useState<Vertical>('agrotech');

  const fetchStatus = useCallback(async (orgId?: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await demoApi.getStatus(orgId);
      setSeeds(data.seeds);
      setSampleFiles(data.sampleFiles);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message || err?.message || 'Failed to load demo status');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrgs = useCallback(async () => {
    setOrgsLoading(true);
    try {
      const data = await demoApi.getOrganisations();
      setOrgs(data);
      if (data.length > 0) {
        // Pick the previously-selected org or default to first
        const id = data.find((o) => o.id === selectedOrgId)?.id ?? data[0].id;
        setSelectedOrgId(id);
        // Fetch seed status scoped to the resolved org right here — no extra effect needed
        await fetchStatus(id);
      } else {
        // No orgs — fetch status with Nabhos Platform fallback
        await fetchStatus(undefined);
      }
    } catch {
      // Non-fatal — org picker will show empty state; still fetch status
      await fetchStatus(undefined);
    } finally {
      setOrgsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount; selectedOrgId intentionally omitted — stale-closure safe via fetchStatus

  // Initial load
  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  // Re-fetch seed status when user picks a different org from the dropdown
  const handleOrgChange = useCallback((orgId: string) => {
    setSelectedOrgId(orgId);
    fetchStatus(orgId);
  }, [fetchStatus]);

  // ── Derived: seeds for the active tab ────────────────────────────────────

  const tabGroups = VERTICAL_SEED_GROUPS[seedTab];
  const tabSeeds = seeds
    .filter((s) => tabGroups.includes(s.vertical))
    .sort((a, b) => a.order - b.order);

  const pendingSeeds = tabSeeds.filter((s) => s.fileExists && !s.applied);
  const appliedSeeds = tabSeeds.filter((s) => s.applied);

  // ── Seed execution ────────────────────────────────────────────────────────

  const runActiveTab = async () => {
    const filesToRun = pendingSeeds.map((s) => s.filename);
    if (filesToRun.length === 0) return;

    setSeeding(true);
    setSeedResults(filesToRun.map((f) => ({ filename: f, status: 'running' })));

    try {
      const { results } = await demoApi.runSeed(filesToRun, selectedOrgId || undefined);
      setSeedResults(results);
    } catch (err: any) {
      setSeedResults(filesToRun.map((f) => ({
        filename: f,
        status: 'error',
        error: err?.response?.data?.error?.message || err?.message || 'Request failed',
      })));
    } finally {
      setSeeding(false);
      fetchStatus(selectedOrgId || undefined);
    }
  };

  // ── Reset ─────────────────────────────────────────────────────────────────

  const handleReset = async () => {
    const orgName = orgs.find((o) => o.id === selectedOrgId)?.name ?? 'Nabhos Platform';
    if (!window.confirm(`This will delete all seeded demo data for "${orgName}". Continue?`)) return;
    setResetting(true);
    setResetDone(false);
    setResetError(null);
    try {
      await demoApi.reset(selectedOrgId || undefined);
      setResetDone(true);
      setSeedResults([]);
      fetchStatus(selectedOrgId || undefined);
    } catch (err: any) {
      setResetError(err?.response?.data?.error?.message || err?.message || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  // ── Derived data ──────────────────────────────────────────────────────────

  const oilGasFiles   = sampleFiles.filter((f) => f.vertical === 'oil-gas');
  const agrotechFiles = sampleFiles.filter((f) => f.vertical === 'agrotech');

  const appliedCount = seeds.filter((s) => s.applied).length;
  const totalSeeds   = seeds.length;

  // ─── Pre-flight checklist items ───────────────────────────────────────────

  const preflightSeedItems = [
    { label: 'Platform URL accessible',  sub: 'Confirm the app loads in your browser',                                                done: true,                                                link: undefined as string | undefined },
    { label: 'Platform admin account',   sub: 'admin@nabhos.com / admin123 — created by seed 01',                                     done: appliedCount >= 1,                                   link: undefined as string | undefined },
    { label: 'Seed data loaded',         sub: `${appliedCount} / ${totalSeeds} seed files applied (see Section 2 below)`,             done: appliedCount === totalSeeds,                         link: undefined as string | undefined },
    { label: 'Sample files available',   sub: `${sampleFiles.filter((f) => f.available).length} / ${sampleFiles.length} on server`,   done: sampleFiles.filter((f) => f.available).length > 0,  link: undefined as string | undefined },
  ];

  const preflightWalkthroughItems = [
    { label: 'Platform URL accessible',       sub: 'Confirm the app loads in your browser',                              done: true,                                                link: undefined as string | undefined },
    { label: 'Platform admin account exists', sub: 'You have email + password for a *.* role account',                   done: true,                                                link: undefined as string | undefined },
    { label: 'Demo org provisioned',          sub: 'Fresh org created with correct vertical enabled',                    done: false,                                               link: '/platform-admin/organizations' as string | undefined },
    { label: 'Demo users registered & roles', sub: 'Pilot, analyst/safety officer, operations lead',                    done: false,                                               link: '/org-admin/users' as string | undefined },
    { label: 'Sample files downloaded',       sub: 'Oil & Gas or AgroTech files saved locally (see Section 3)',          done: sampleFiles.filter((f) => f.available).length > 0,  link: undefined as string | undefined },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-8">

      {/* ── Page header ───────────────────────────────────────────────── */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <FlaskConical size={22} className="text-amber-600" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Demo Setup</h1>
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 font-medium">
              Platform Admin
            </span>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-2xl">
            Set up demo environments for sales presentations without requiring git access.
            Load seed data, download sample upload files, and follow the interactive runbook — all from here.
          </p>
        </div>
        <button
          onClick={() => fetchStatus(selectedOrgId || undefined)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {error && <Alert type="error">{error}</Alert>}

      {/* ═══════════════════════════════════════════════════════════════
          OPTION A — SEED APPROACH
          Blue left-border track. Contains: checklist + seed loader + teardown.
      ══════════════════════════════════════════════════════════════════ */}
      <div className="border border-blue-200 dark:border-blue-800/50 rounded-xl overflow-hidden">

        {/* Track header */}
        <div className="flex items-start gap-3 px-5 py-4 bg-blue-50 dark:bg-blue-950/20 border-b border-blue-200 dark:border-blue-800/50">
          <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/40 mt-0.5 flex-shrink-0">
            <Database size={16} className="text-blue-600 dark:text-blue-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-blue-900 dark:text-blue-200">
              Option A — Seed Approach
            </h2>
            <p className="text-xs text-blue-700 dark:text-blue-400 mt-0.5">
              Seed scripts populate a <strong>demo organisation</strong> with all demo data in one click.
              Only organisations flagged as <em>Demo</em> in{' '}
              <Link to="/platform-admin/organizations" className="underline hover:text-blue-600">Organisation Provisioning</Link>{' '}
              appear in the dropdown below — production orgs are never shown here.
              Select the target org, choose a vertical tab, and click <strong>Run seeds</strong>.
            </p>
          </div>
        </div>

        {/* ── Option A body: 1/3 left + 2/3 right two-column layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-blue-100 dark:divide-blue-800/30 bg-white dark:bg-gray-900">

          {/* LEFT COL — checklist + teardown */}
          <div className="px-5 py-5 space-y-6">

            {/* A1. Pre-flight checklist */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Pre-flight checklist</p>
              <div className="space-y-1">
                {preflightSeedItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
                    <div className={`mt-0.5 flex-shrink-0 ${item.done ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'}`}>
                      {item.done ? <CheckCircle2 size={15} /> : <Clock size={15} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-medium ${item.done ? 'text-gray-700 dark:text-gray-300' : 'text-gray-600 dark:text-gray-400'}`}>{item.label}</span>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{item.sub}</p>
                    </div>
                    {item.link && (
                      <Link to={item.link} className="flex-shrink-0 text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">Go <ExternalLink size={10} /></Link>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* A3. Teardown */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Teardown</p>
              {resetDone && <Alert type="success">All demo data wiped. The environment is ready for a fresh demo.</Alert>}
              {resetError && <Alert type="error">{resetError}</Alert>}
              <div className="rounded-lg border border-red-100 dark:border-red-900/30 bg-red-50 dark:bg-red-950/10 p-3 space-y-2">
                <div className="flex items-center gap-1.5">
                  <AlertTriangle size={13} className="text-red-500 flex-shrink-0" />
                  <p className="text-xs font-medium text-red-700 dark:text-red-400">This action cannot be undone</p>
                </div>
                <p className="text-xs text-red-600 dark:text-red-500 leading-relaxed">
                  Deletes all missions, anomalies, boundaries, imagery, assets and hierarchy nodes seeded into{' '}
                  <strong>{orgs.find((o) => o.id === selectedOrgId)?.name ?? 'Nabhos Platform'}</strong>.
                </p>
                <button
                  onClick={handleReset}
                  disabled={resetting}
                  className="w-full flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {resetting ? <RefreshCw size={12} className="animate-spin" /> : <Trash2 size={12} />}
                  {resetting ? 'Wiping…' : 'Wipe Demo Data'}
                </button>
              </div>
            </div>

          </div>

          {/* RIGHT COL (2/3) — seed loader */}
          <div className="lg:col-span-2 px-5 py-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Seed data</p>

            {/* Org picker */}
            <div className="mb-4 p-3 rounded-lg border border-blue-100 dark:border-blue-800/40 bg-blue-50/50 dark:bg-blue-950/10">
              <div className="flex items-center gap-2 mb-2">
                <Building2 size={14} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <span className="text-xs font-semibold text-blue-800 dark:text-blue-300">Target organisation</span>
              </div>
              {orgsLoading ? (
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <RefreshCw size={12} className="animate-spin" /> Loading organisations…
                </div>
              ) : orgs.length === 0 ? (
                <div className="flex items-start gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40">
                  <AlertTriangle size={14} className="text-amber-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-700 dark:text-amber-300">
                    No demo organisations found.{' '}
                    <Link to="/platform-admin/organizations" className="font-medium underline hover:text-amber-900 dark:hover:text-amber-100">
                      Create one in Organisation Provisioning
                    </Link>
                    {' '}and enable the <strong>Demo organisation</strong> toggle when creating it.
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="relative flex-1 min-w-[240px]">
                    <select
                      value={selectedOrgId}
                      onChange={(e) => handleOrgChange(e.target.value)}
                      className="w-full pl-3 pr-8 py-2 text-sm rounded-md border border-blue-200 dark:border-blue-700 bg-white dark:bg-gray-900 text-gray-800 dark:text-gray-200 appearance-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                      {orgs.map((org) => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                    <ChevronDown size={13} className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                  {selectedOrgId && (
                    <span className="text-xs text-blue-700 dark:text-blue-400 flex items-center gap-1">
                      <CheckCircle2 size={12} />
                      Seeds will target <strong>{orgs.find((o) => o.id === selectedOrgId)?.name ?? '…'}</strong>
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Tabs */}
            <div className="flex gap-0 border-b border-gray-200 dark:border-gray-700">
              {SEED_TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => { setSeedTab(tab.key); setSeedResults([]); }}
                  className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    seedTab === tab.key
                      ? `${tab.tabColor} border-current bg-white dark:bg-gray-900`
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {tab.icon}{tab.label}
                </button>
              ))}
            </div>

            {/* Tab panel */}
            {(() => {
              const tab = SEED_TABS.find((t) => t.key === seedTab)!;
              return (
                <div className="border border-t-0 border-gray-200 dark:border-gray-700 rounded-b-lg p-4">
                  <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">{tab.purpose}</p>
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Screens populated</p>
                    <div className="flex flex-wrap gap-1.5">
                      {tab.screens.map((s) => (
                        <span key={s} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">{s}</span>
                      ))}
                    </div>
                  </div>
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                      SQL files that will be executed
                      {pendingSeeds.length === 0 && appliedSeeds.filter((s) => s.fileExists).length > 0 && (
                        <span className="ml-2 font-normal normal-case text-green-600 dark:text-green-400">— all already applied ✓</span>
                      )}
                    </p>
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">
                            <th className="px-3 py-2 text-left font-medium w-8">#</th>
                            <th className="px-3 py-2 text-left font-medium">File</th>
                            <th className="px-3 py-2 text-left font-medium">Creates</th>
                            <th className="px-3 py-2 text-left font-medium w-24">Vertical</th>
                            <th className="px-3 py-2 text-left font-medium w-28">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                          {tabSeeds.map((s) => {
                            const result = seedResults.find((r) => r.filename === s.filename);
                            return (
                              <tr
                                key={s.filename}
                                className={s.applied && s.fileExists ? 'bg-green-50/50 dark:bg-green-950/10' : !s.fileExists ? 'opacity-50' : 'bg-white dark:bg-gray-900'}
                              >
                                <td className="px-3 py-2 text-gray-400">{s.order}</td>
                                <td className="px-3 py-2 font-mono text-gray-700 dark:text-gray-300">{s.filename}</td>
                                <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{s.description}</td>
                                <td className="px-3 py-2">
                                  <span className={`px-1.5 py-0.5 rounded font-medium ${GROUP_BADGE[s.vertical].color}`}>{GROUP_BADGE[s.vertical].label}</span>
                                </td>
                                <td className="px-3 py-2">
                                  {result ? (
                                    result.status === 'running' ? (
                                      <span className="flex items-center gap-1 text-blue-500"><RefreshCw size={11} className="animate-spin" />Running…</span>
                                    ) : result.status === 'ok' ? (
                                      <span className="flex items-center gap-1 text-green-600"><CheckCircle2 size={11} />Done</span>
                                    ) : (
                                      <span className="flex items-center gap-1 text-red-500" title={result.error}><XCircle size={11} />Error</span>
                                    )
                                  ) : !s.fileExists ? (
                                    <span className="flex items-center gap-1 text-gray-400"><AlertTriangle size={11} />Not on server</span>
                                  ) : s.applied ? (
                                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400"><CheckCircle2 size={11} />Already applied</span>
                                  ) : (
                                    <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400"><Clock size={11} />Pending</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {seedResults.some((r) => r.status === 'error') && (
                      <div className="mt-2 space-y-1">
                        {seedResults.filter((r) => r.status === 'error').map((r) => (
                          <p key={r.filename} className="text-xs text-red-600 dark:text-red-400">
                            <span className="font-mono">{r.filename}</span>: {r.error}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-800">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {pendingSeeds.length > 0
                        ? `${pendingSeeds.length} file${pendingSeeds.length !== 1 ? 's' : ''} ready to run against ${orgs.find((o) => o.id === selectedOrgId)?.name ?? 'the target org'}.`
                        : tabSeeds.every((s) => !s.fileExists)
                        ? 'Seed files are not present on this server — deploy them to enable seeding.'
                        : appliedSeeds.filter((s) => s.fileExists).length > 0
                        ? `All ${appliedSeeds.filter((s) => s.fileExists).length} available seed${appliedSeeds.filter((s) => s.fileExists).length !== 1 ? 's' : ''} already applied to ${orgs.find((o) => o.id === selectedOrgId)?.name ?? 'this org'}.`
                        : 'No seed files available on this server.'}
                    </p>
                    <button
                      disabled={seeding || pendingSeeds.length === 0}
                      onClick={runActiveTab}
                      title={
                        pendingSeeds.length > 0 ? undefined
                        : tabSeeds.every((s) => !s.fileExists) ? 'Seed SQL files are not deployed on this server'
                        : 'All seeds already applied to this org'
                      }
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${tab.btnColor}`}
                    >
                      {seeding ? (
                        <><RefreshCw size={14} className="animate-spin" />Running…</>
                      ) : pendingSeeds.length > 0 ? (
                        <><Database size={14} />Run {pendingSeeds.length} seed{pendingSeeds.length !== 1 ? 's' : ''}</>
                      ) : tabSeeds.every((s) => !s.fileExists) ? (
                        <><AlertTriangle size={14} />Files not on server</>
                      ) : (
                        <><CheckCircle2 size={14} />Already applied</>
                      )}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>

        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          OPTION B — LIVE WALKTHROUGH
          Green left-border track. Contains: checklist + sample files + runbook.
      ══════════════════════════════════════════════════════════════════ */}
      <div className="border border-green-200 dark:border-green-800/50 rounded-xl overflow-hidden">

        {/* Track header */}
        <div className="flex items-start gap-3 px-5 py-4 bg-green-50 dark:bg-green-950/20 border-b border-green-200 dark:border-green-800/50">
          <div className="p-1.5 rounded-lg bg-green-100 dark:bg-green-900/40 mt-0.5 flex-shrink-0">
            <FileText size={16} className="text-green-600 dark:text-green-400" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-semibold text-green-900 dark:text-green-200">
              Option B — Live Walkthrough
            </h2>
            <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">
              Provision a fresh named organisation (e.g. <em>TransNiger Energy Ltd</em>), register demo users,
              and walk through each step live in front of the customer. Download the sample files below
              to use during the upload step. No seed data is loaded — every record is created in real time.
            </p>
          </div>
        </div>

        {/* ── Option B body: 1/3 left + 2/3 right two-column layout ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 divide-y lg:divide-y-0 lg:divide-x divide-green-100 dark:divide-green-800/30 bg-white dark:bg-gray-900">

          {/* LEFT COL — checklist + sample files + teardown */}
          <div className="px-5 py-5 space-y-6">

            {/* B1. Pre-flight checklist */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Pre-flight checklist</p>
              <div className="space-y-1">
                {preflightWalkthroughItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
                    <div className={`mt-0.5 flex-shrink-0 ${item.done ? 'text-green-500' : 'text-gray-300 dark:text-gray-600'}`}>
                      {item.done ? <CheckCircle2 size={15} /> : <Clock size={15} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className={`text-sm font-medium ${item.done ? 'text-gray-700 dark:text-gray-300' : 'text-gray-600 dark:text-gray-400'}`}>{item.label}</span>
                      <p className="text-xs text-gray-400 dark:text-gray-500">{item.sub}</p>
                    </div>
                    {item.link && (
                      <Link to={item.link} className="flex-shrink-0 text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1">Go <ExternalLink size={10} /></Link>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* B2. Sample file downloads */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                Sample upload files
                <span className="ml-1 font-normal normal-case text-gray-400">— for the upload step</span>
              </p>

              {/* Oil & Gas */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <Flame size={13} className="text-blue-600" />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Oil & Gas</span>
                  </div>
                  <button onClick={() => demoApi.downloadZip('oil-gas')} className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 font-medium">
                    <FileArchive size={11} /> .zip
                  </button>
                </div>
                <div className="space-y-1">
                  {oilGasFiles.map((f) => (
                    <div key={f.filename} className={`flex items-center gap-2 p-2 rounded border ${f.available ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900' : 'border-gray-100 dark:border-gray-800 opacity-50'}`}>
                      <div className="flex-shrink-0">{fileIcon(f.filename)}</div>
                      <p className="flex-1 text-xs font-mono text-gray-700 dark:text-gray-300 truncate min-w-0">{f.filename}</p>
                      {f.available
                        ? <button onClick={() => demoApi.downloadFile('oil-gas', f.filename)} className="flex-shrink-0 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600"><Download size={12} /></button>
                        : <AlertTriangle size={12} className="flex-shrink-0 text-gray-300" />}
                    </div>
                  ))}
                </div>
              </div>

              {/* AgroTech */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-1.5">
                    <Leaf size={13} className="text-green-600" />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">AgroTech</span>
                  </div>
                  <button onClick={() => demoApi.downloadZip('agrotech')} className="flex items-center gap-1 text-xs px-2 py-1 rounded bg-green-600 text-white hover:bg-green-700 font-medium">
                    <FileArchive size={11} /> .zip
                  </button>
                </div>
                <div className="space-y-1">
                  {agrotechFiles.map((f) => (
                    <div key={f.filename} className={`flex items-center gap-2 p-2 rounded border ${f.available ? 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900' : 'border-gray-100 dark:border-gray-800 opacity-50'}`}>
                      <div className="flex-shrink-0">{fileIcon(f.filename)}</div>
                      <p className="flex-1 text-xs font-mono text-gray-700 dark:text-gray-300 truncate min-w-0">{f.filename}</p>
                      {f.available
                        ? <button onClick={() => demoApi.downloadFile('agrotech', f.filename)} className="flex-shrink-0 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600"><Download size={12} /></button>
                        : <AlertTriangle size={12} className="flex-shrink-0 text-gray-300" />}
                    </div>
                  ))}
                </div>
                {sampleFiles.some((f) => !f.available) && (
                  <p className="mt-2 text-xs text-gray-400 flex items-center gap-1">
                    <AlertTriangle size={11} /> Some files not yet on server.
                  </p>
                )}
              </div>
            </div>

            {/* B4. Teardown */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Teardown</p>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-3 space-y-2">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Delete the demo organisation</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  Deleting the provisioned org cascades all data beneath it — users, missions, boundaries, assets, and hierarchy. No separate wipe needed.
                </p>
                <Link
                  to="/platform-admin/organizations"
                  className="flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <ExternalLink size={12} /> Manage Orgs
                </Link>
              </div>
            </div>

          </div>

          {/* RIGHT COL (2/3) — runbook */}
          <div className="lg:col-span-2 px-5 py-5">
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">Step-by-step runbook</p>
            <div className="flex gap-1 mb-4 p-1 bg-gray-100 dark:bg-gray-800 rounded-lg w-fit">
              {([['agrotech', 'AgroTech', <Leaf size={14} />], ['oil-gas', 'Oil & Gas', <Flame size={14} />]] as [Vertical, string, React.ReactNode][]).map(([key, label, icon]) => (
                <button
                  key={key}
                  onClick={() => setRunbookTab(key as Vertical)}
                  className={`flex items-center gap-1.5 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    runbookTab === key
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {icon} {label}
                </button>
              ))}
            </div>
            <RunbookViewer
              steps={runbookTab === 'agrotech' ? RUNBOOK_AGROTECH : RUNBOOK_OIL_GAS}
              vertical={runbookTab}
            />
          </div>

        </div>
      </div>

    </div>
  );
}
